// The workspace's operational home: every screen's live state at a glance,
// with the daily controls (offset) inline. Configuration lives in the Locations
// and Templates sections; this page is where an operator lands.

import { ErrorPage, PageHead, Spinner } from "@pej/kore/components";
import { createFileRoute } from "@tanstack/react-router";
import { useWorkspaceStatus } from "@/api/status";
import { ProblemsCard } from "@/features/overview/problems-card";
import { ScreenStatusCard } from "@/features/overview/screen-status-card";
import { describeStatusError } from "@/features/overview/status-error";

export const Route = createFileRoute("/w/$workspaceId/")({
    component: OverviewPage,
});

function OverviewPage() {
    const { workspaceId } = Route.useParams();
    const statusQuery = useWorkspaceStatus(workspaceId);

    if (statusQuery.isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (statusQuery.isError) {
        const { status, pd } = describeStatusError(statusQuery.error);
        // No PageHead here: ErrorPage renders its own <h1> for what went
        // wrong, and a second <h1> saying "Overview" above it would be a
        // duplicate top-level heading (a real a11y defect, not just visual
        // clutter). `scope` carries the "you're still on Overview"
        // orientation instead, next to the status badge.
        return (
            <ErrorPage
                status={status}
                pd={pd}
                scope="Overview"
                primary={{ label: "Try again", onClick: () => void statusQuery.refetch() }}
            />
        );
    }

    const rows = statusQuery.data ?? [];

    return (
        <div className="flex flex-col gap-6">
            <PageHead
                size="h1"
                title="Overview"
                description="What every screen shows right now, how fresh it is, and the quick offset."
            />
            <ProblemsCard workspaceId={workspaceId} rows={rows} />
            {rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                    No screens yet. Create a location and its first screen under Locations.
                </p>
            ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                    {rows.map((row) => (
                        <ScreenStatusCard key={row.screen.id} workspaceId={workspaceId} row={row} />
                    ))}
                </div>
            )}
        </div>
    );
}
